import React from 'react';
import { object, string, array, bool } from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { parse } from 'libphonenumber-js';
import { compose, head, isEmpty, isNil, partial, values, startsWith, replace } from 'ramda';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import PhoneIcon from '@material-ui/icons/Phone';
import CountrySelector from './CountrySelector';
import { formatMessage, flipPick, toArray } from '../../../utils/helpers';
import { parsePhoneNumber, PARSE_TYPE } from '../../../utils/normalizing';
import { isValidPhoneNumber, REQUIRED_ERROR } from '../../../utils/validation';
import generalMessages from '../../../messages';
import messages from './messages';
import { useConnect } from './hooks';
import useStyles from './styles';

const PhoneNumber = ({
  className,
  style,
  country,
  formik,
  id,
  intl,
  label,
  labelStyle,
  iconHidden,
  required,
  shrink = true,
  preferredCountries = [],
}) => {
  const { isDownsized } = useConnect();
  const classes = useStyles({ isDownsized });
  const getIntlMessage = partial(formatMessage, [intl]);
  const placeholder = compose(
    getIntlMessage,
    compose(
      head,
      values,
      flipPick(messages),
      toArray,
    ),
  )(country);

  const isError = field => {
    if (formik.touched[field] || !isEmpty(formik.values[field])) return formik.errors[field];
    return undefined;
  };

  const onCountryChange = phoneCountry => {
    if (country !== phoneCountry) {
      formik.setFieldValue('phoneCountry', phoneCountry);
      formik.setFieldValue('phoneNumber', '');
    }
  };

  const normalize = value => {
    const phoneNumber = startsWith('00', value) ? replace(/00/, '+', value) : value;
    const phoneCountryCode = parse(phoneNumber)?.country?.toLowerCase() || country;
    const isValid = isValidPhoneNumber(phoneNumber, phoneCountryCode);
    formik.setFieldValue('phoneCountry', phoneCountryCode);
    return isValid ? parsePhoneNumber(phoneNumber, phoneCountryCode, PARSE_TYPE.INTERNATIONAL) : phoneNumber;
  };

  return (
    <Grid
      container
      alignItems={isError(id) ? 'center' : 'flex-end'}
      className={className}
      style={style}
      justify="space-between"
      spacing={2}
    >
      {!iconHidden && (
        <Grid item className={classes.align}>
          <PhoneIcon fontSize="small" className="icon icon-field" color="secondary" />
        </Grid>
      )}
      <Grid item container alignItems="center" xs className={classes.countrySelector}>
        <CountrySelector
          formik={formik}
          onChange={onCountryChange}
          value={country}
          preferredCountries={preferredCountries}
        />
        <TextField
          error={!isNil(isError(id))}
          fullWidth
          helperText={
            isError(id) &&
            (isError(id) === REQUIRED_ERROR ? (
              <FormattedMessage {...generalMessages[isError(id)]} />
            ) : (
              <FormattedMessage {...messages[isError(id)]} />
            ))
          }
          id={id}
          InputLabelProps={shrink ? { shrink: true, classes: { root: labelStyle } } : null}
          label={
            <>
              {getIntlMessage(label || messages.label)}
              {required && <span className={classes.required} />}
            </>
          }
          onBlur={e => {
            e.target.value = normalize(e.target.value);
            formik.handleBlur(e);
            formik.handleChange(e);
          }}
          onChange={formik.handleChange}
          placeholder={placeholder}
          type="tel"
          value={formik.values[id]}
        />
      </Grid>
    </Grid>
  );
};

PhoneNumber.propTypes = {
  className: string,
  style: object,
  country: string.isRequired,
  formik: object,
  id: string,
  intl: object,
  label: object,
  labelStyle: string,
  iconHidden: bool,
  required: bool,
  shrink: bool,
  preferredCountries: array,
};

export default injectIntl(PhoneNumber);
